import { ECaptchaField } from "../enum/e-captcha-field";
import { AValidator } from "./a-validator";
import { fromEvent, lastValueFrom, mergeMap, take } from "rxjs";
import { CaptchaRepo } from "../../../utils/typescript/repositories/captcha.repo";
import { DomService } from "../../../utils/typescript/service/dom.service";

export class CaptchaValidator extends AValidator {
  protected formElement: HTMLFormElement;
  protected elements: Map<ECaptchaField, unknown> = new Map();

  constructor(formElement: HTMLFormElement) {
    super();

    this.formElement = formElement;
    this.buildElementList();

    this.addEvents();
    this.showCaptcha();
  }

  getValidationFunctions() {
    return [
      // need to bind, because these methods used as callback
      this.validationCaptcha.bind(this),
    ];
  }

  protected showCaptcha() {
    (
      this.elements.get(ECaptchaField.CAPTCHA_CONTAINER) as HTMLDivElement
    ).classList.remove("hidden");
  }

  protected addEvents() {
    fromEvent(<HTMLDivElement>this.elements.get(ECaptchaField.REFRESH), "click")
      .pipe(mergeMap(() => CaptchaRepo.getCaptchaV2Page()))
      .subscribe((result) => this.refresh(result));
  }

  protected buildElementList() {
    const list = new Map();
    list.set(
      ECaptchaField.CAPTCHA_CONTAINER,
      this.formElement.querySelector<HTMLDivElement>(".form-field-captcha"),
    ); // main element

    if (!list.get(ECaptchaField.CAPTCHA_CONTAINER)) {
      return;
    }

    // captcha elements
    list.set(
      ECaptchaField.CAPTCHA_HASH,
      list
        .get(ECaptchaField.CAPTCHA_CONTAINER)
        .querySelector('input[name="captchaHash"]'),
    );
    list.set(
      ECaptchaField.CAPTCHA_IMAGE,
      list.get(ECaptchaField.CAPTCHA_CONTAINER).querySelector(".captcha-image"),
    );
    list.set(
      ECaptchaField.CAPTCHA_RESULT,
      list
        .get(ECaptchaField.CAPTCHA_CONTAINER)
        .querySelector('input[name="captchaResult"]'),
    );
    list.set(
      ECaptchaField.REFRESH,
      list.get(ECaptchaField.CAPTCHA_CONTAINER).querySelector(".refresh"),
    );

    // errors
    list.set(
      ECaptchaField.ERROR_LIST_ELEMENTS,
      list.get(ECaptchaField.CAPTCHA_CONTAINER).querySelectorAll(".hint-list"),
    );

    this.elements = list;
  }

  protected async validationCaptcha(
    setFieldHintCb: (
      errorNumber: Array<number>,
      element: Array<HTMLElement>,
    ) => void,
  ) {
    const captchaHashElement = this.elements.get(
      ECaptchaField.CAPTCHA_HASH,
    ) as HTMLInputElement;
    const captchaResultElement = this.elements.get(
      ECaptchaField.CAPTCHA_RESULT,
    ) as HTMLInputElement;

    if (!captchaHashElement || !captchaResultElement) {
      setFieldHintCb([5], [captchaResultElement]);
      return;
    }

    const params = new URLSearchParams();
    params.set("captchaHash", captchaHashElement.value);
    params.set("captchaResult", captchaResultElement.value);

    const isValid = await lastValueFrom(
      CaptchaRepo.validateCaptcha(params).pipe(take(1)),
    );

    if (!isValid) {
      setFieldHintCb([5], [captchaResultElement]);
    }
  }

  protected refresh(pageAsString: string) {
    const wrapper = (
      this.elements.get(ECaptchaField.CAPTCHA_CONTAINER) as HTMLDivElement
    ).querySelector(".captcha-wrapper");
    if (wrapper)
      wrapper.innerHTML = DomService.removeScriptsFromString(pageAsString);

    // update cached map elements
    this.buildElementList();
    this.addEvents();
  }
}
